import { useIdentity } from 'base/identity-resolver';
import { useState, useEffect } from 'react';
import { getUserContactIdForOrg } from 'apps/tasks/utils/contact-helpers';
import EverTrue from 'app';
import Api from 'entities/helpers/api';
import {
  createNewRecommendedActions,
  getExistingRecommendedActions,
} from 'apps/home/utils/api-requests/recommended-actions/use-recommended-actions-requests';

// Key: Name of Section
// Value: User Setting Configuration associated with Section
const SECTION_TO_SETTINGS_MAPPINGS = {
  recent_gift: 'enableGiftSection',
  career_move: 'enableCareerMovesSection',
  gift_anniversary: 'enableAnniversarySection',
  trip_meeting: 'enableTripMeeting',
  dps: 'enableDPS',
};

// Key: Name of Section
// Value: an empty array (default state)
// NOTE: This is the order in which the items will be rendered
const RECOMMENDATION_SECTIONS = {
  recent_gift: [],
  career_move: [],
  gift_anniversary: [],
  trip_meeting: [],
  dps: [],
};

const useRecommendedActions = (userSettings) => {
  const [empty, setEmpty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recommendedActions, setRecommendedActions] = useState(RECOMMENDATION_SECTIONS);
  const { user, org } = useIdentity();

  useEffect(() => {
    const getAndSetRecommendedActions = async () => {
      const actions = {};

      try {
        setLoading(true);
        const userSolicitorContactId = getUserContactIdForOrg(user, org);
        if (!userSolicitorContactId) return;
        const enabledSegments = Object.keys(RECOMMENDATION_SECTIONS).filter(
          (section) => userSettings[SECTION_TO_SETTINGS_MAPPINGS[section]]
        );
        const existingRecommendedActions = await getExistingRecommendedActions(enabledSegments);
        existingRecommendedActions.forEach((rec) => {
          const segment = rec?.segment;
          const recActions = rec?.actions || [];
          actions[segment] = recActions || [];
        });

        await createNewRecommendedActions(enabledSegments, userSolicitorContactId, userSettings, actions);
        setRecommendedActions(actions);
        const showEmpty = !Object.values(actions).flat().length;
        setEmpty(showEmpty);
      } finally {
        setLoading(false);
      }
    };

    getAndSetRecommendedActions();
  }, [org, user, userSettings]);

  const handleBulkViewClick = async () => {
    if (!recommendedActions || typeof recommendedActions !== 'object') return;

    // Flatten all action arrays and extract their IDs
    const ids = Object.values(recommendedActions)
      .flat()
      .map((action) => action.id)
      .filter(Boolean); // Remove any undefined/null

    try {
      setLoading(true);

      // The API returns an array of successfully updated IDs
      const successfulIds = await Api.JOURNEYS.RECOMMENDED_ACTIONS_BULK_VIEW.put({
        data: JSON.stringify(ids),
      });

      // Show alert based on result
      if (successfulIds?.length === ids?.length) {
        EverTrue.Alert.success('All recommended actions successfully marked as viewed.');
      } else {
        EverTrue.Alert.error(
          `${ids.length - successfulIds.length} recommended action(s) could not be marked as viewed.`
        );
      }

      // Only keep actions that were NOT successful
      setRecommendedActions((previousState) => {
        const newState = Object.fromEntries(
          Object.entries(previousState).map(([key, value]) => [
            key,
            value.filter((ra) => !successfulIds.includes(ra.id)),
          ])
        );
        // Check if all arrays are empty
        const isEmpty = !Object.values(newState).flat().length;
        setEmpty(isEmpty);
        return newState;
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    empty,
    loading,
    recommendedActions,
    handleBulkViewClick,
  };
};

export default useRecommendedActions;
